import axios, {AxiosRequestConfig} from 'axios';
import {clearCache, encodeGetParams, getUserToken} from './common';
import { message } from 'antd';
import {getQueryParams, ObjTest} from 'jzfz-web-utils';
import { createHashHistory } from 'history';
import commonConfig from "../config/common";
import {EnvEnum, GLOBAL_ENV, appId} from "../config/constant";
import {GLOBAL_WEBSITE_CMS_URL} from "../config/api";


const history = createHashHistory();
// axios实例
const instance = axios.create();
// 登出防抖
let logoutDebounce = null;
// 接口返回状态码枚举
export enum APIStatusEnum {
	error = 0,  // 发生错误
	SUCCESS = 1,	// 成功
	notLogin = 401, // 未登录
	forbidden = 403,    // 拒绝访问
}


// 请求配置接口
interface IRequestOptions extends AxiosRequestConfig {
	callback?: (info: any) => void;
	isLog?: boolean;    // 是否是日志类型请求(不对接口响应做任何处理)
	onUploadProgress?: (progressEvent: any) => void;
}
// 文件上传配置接口
interface IFileUploadOptions extends IRequestOptions {
	baseURL?: string;
	onUploadProgress?: (progressEvent: any) => void;
}
// 文件下载配置接口
interface IFileDownOptions extends IRequestOptions {
	filename?: string;  // 文件名
}


/**
 * 获取请求头
 */
export const getHeaders = (): any => {
	let token = getUserToken();
	let headers: any = {
		Authorization: 'Bearer ' + token,
	}

	if (commonConfig.appId) {
		headers = {...headers, AppId: commonConfig.appId};
	}

	return headers;
}


/**
 * 服务器状态码检查
 * @param status
 */
const httpStatusCheck = (status) => {
	let msg = '连接服务器失败';
	switch (status) {
		case 400: msg = '请求错误(400)' ; break;
		case 401: msg = '未授权，请重新登录(401)'; break;
		case 403: msg = '拒绝访问(403)'; break;
		case 404: msg = '请求出错(404)'; break;
		case 408: msg = '请求超时(408)'; break;
		case 500: msg = '服务器错误(500)'; break;
		case 501: msg = '服务未实现(501)'; break;
		case 502: msg = '网络错误(502)'; break;
		case 503: msg = '服务不可用(503)'; break;
		case 504: msg = '网络超时(504)'; break;
		case 505: msg = 'HTTP版本不受支持(505)'; break;
		default: msg = `连接出错(${status})!`;
	}
	message.error(msg + '，请稍后再试');
	return Promise.reject(msg);
}


/**
 * 路由重定向
 */
const routeRedirect = () => {
	const history = createHashHistory();
	const { pathname } = history.location;
	if (logoutDebounce) {
		clearTimeout(logoutDebounce);
	}
	if (pathname === '/login') {
		return;
	}

	logoutDebounce = setTimeout(() => {
		const { pathname } = history.location;
		clearCache();
		history.replace({pathname: '/login', search: encodeGetParams({...getQueryParams(), from: pathname})});
	}, 3000)
}


/**
 * 接口状态码检查
 * @param res
 * @param config
 */
const codeCheck = (res: any, config: IRequestOptions) : Promise<any> => {
	if (res.status == APIStatusEnum.notLogin) {
		message.warning(res.info, 3);
		routeRedirect();
		return Promise.reject(res.info);
	}
	if (res.status == APIStatusEnum.error || res.status === APIStatusEnum.forbidden) {
		if (config.isLog) {
			return Promise.reject(res.info);
		}
		if (config.callback && typeof config.callback == 'function') {
			config.callback(res);
		}else {
			message.error(res.info);
		}
		return Promise.reject(res.info);
	}
	return res;
}


/**
 * Blob转Json
 * @param data
 */
const blobToJson = (data: Blob): Promise<any> => {
	return new Promise((resolve: any, reject: any) => {
		if (data.type !== 'application/json') {
			reject('非json数据, 转换失败');
			return;
		}
		let reader: any = new FileReader();
		reader.onload = () => {
			let result = reader.result;
			let jsonObj = JSON.parse(result);
			resolve(jsonObj);
		}
		reader.onerror = () => {
			reject('Blob转json失败');
		}
		reader.readAsText(data);
	})
}


/**
 * 自定义参数序列化
 */
const paramsSerializer = (params): string => {
	return encodeGetParams(params);
}


/**
 * 接口日志
 * @param res
 */
const apiLog = (res: any) => {
	// 开发、测试环境才打印日志
	if (GLOBAL_ENV === EnvEnum.DEV || GLOBAL_ENV === EnvEnum.TEST) {
		if (!res) {
			return;
		}
		const { data: responseData, config: {url, headers, data, params}, status: httpCode } = res;
		if (httpCode !== 200 || (ObjTest.isObj(responseData) && responseData.status !== APIStatusEnum.SUCCESS)) {
			const { info } = responseData || {};
			let logInfo = [
				`【${httpCode === 200 ? '接口异常' : '服务器错误'}】\n`,
				`HttpCode: ${httpCode}\n`,
				`接口路径: ${url}\n`,
				`请求头: ${JSON.stringify(headers)}\n`,
				`请求参数: ${data || params || ''}\n`,
				`响应信息: ${info || ''}`
			].join('');
			console.info(`%c${logInfo}`, 'color: red;font-size: 16px;font-weight: bold;');
		}
	}
}


/**
 * 定义响应拦截器
 */
instance.interceptors.response.use((res: any) => {
	apiLog(res);
	return res;
}, (err: any) => {
	if (axios.isCancel(err)) {
		return Promise.reject('请求已取消');
	}

	apiLog(err.response);
	if (err.response && 'status' in err.response) {
		return httpStatusCheck(err.response.status);
	}
	message.error('服务器无响应');
	return Promise.reject('服务器无响应');
});


/**
 * 自定义请求函数
 * @param url
 * @param body
 * @param options
 */
export const request = (url: string, body: object = {}, options?: IRequestOptions): Promise<any> => {
	const { method = 'post', isLog, callback, onUploadProgress, ...restOptions } = options || {};
	return instance({
		url,
		method,
		data: (method.toLowerCase() == 'post' ? body : {}),
		params: (method.toLowerCase() == 'get' ? body : {}),
		timeout: 200000,
		headers: getHeaders(),
		paramsSerializer,
		...restOptions,
	}).then((res: any) => {
		return codeCheck(res.data, options);
	})
}


/**
 * 获取文件上传query参数
 * @param options
 */
export const getFileUploadParams = (options: IRequestOptions): any => {
    return { menus: 'officialWebsiteAdmin', appId };
}


/**
 * 上传文件
 * @param url
 * @param file
 * @param key
 * @param options
 */
export const fileUpload = (url: string, file: any, key: string, options?: IFileUploadOptions) : Promise<any> => {
	const { data, method = 'post', params, baseURL = GLOBAL_WEBSITE_CMS_URL, ...restOptions} = options || {};
	const { appId, menus, } = commonConfig;

	let formData = new FormData();
	formData.append(key, file);

	//如果有额外数据信息，放入formData传递过去
	if(!ObjTest.isEmptyObject(data)) {
		for (let k in data) {
			formData.append(k, data[k]);
		}
	}
	return instance({
		url,
		baseURL,
		method,
		data: formData,
		params: {...params, ...getFileUploadParams(options)},
		timeout: 200000,
		headers: {'Content-Type':'multipart/form-data', ...getHeaders()},
		paramsSerializer,
		...restOptions,
	}).then(res => {
		const { success, info, status } = res.data;
		if (success === false) {
			message.error(info);
			return Promise.reject(info);
		}
		// 检查接口返回状态(兼容java输出模型)
		if (status !== null && status !== undefined) {
			return codeCheck(res.data, {});
		}
		return res.data;
	})
}


/**
 * 删除文件
 * @param url
 * @param body
 * @param options
 */
export const fileRemove = (url: string, body: any, options?: IRequestOptions) : Promise<any> => {
	const { method = 'post', baseURL = GLOBAL_WEBSITE_CMS_URL, ...restOptions } = options || {};

	return instance({
		url,
		baseURL,
		method,
		data: (method.toLowerCase() == 'post' ? body : {}),
		params: (method.toLowerCase() == 'get' ? body : {}),
		timeout: 200000,
		headers: getHeaders(),
		paramsSerializer,
		...restOptions,
	}).then(res => {
		const { success, info } = res.data;
		if (success === false) {
			message.error(info);
			return Promise.reject(info);
		}
		return res.data;
	})
}


/**
 * 下载文件
 * @param url
 * @param body
 * @param options
 */
export const fileDownload = (url: string, body: any, options?: IFileDownOptions): Promise<any> => {
	const { method = 'post', filename: customFilename, baseURL = GLOBAL_WEBSITE_CMS_URL, ...restOptions } = options || {};
	return instance({
		url,
		baseURL,
		method,
		data: (method.toLowerCase() == 'post' ? body : {}),
		params: (method.toLowerCase() == 'get' ? body : {}),
		timeout: 200000,
		headers:  getHeaders(),
		responseType: 'blob',
		paramsSerializer,
		...restOptions,
	})
		.then(res => {
			// 获取文件名
			const { filename } = res.headers;

			// 接口响应json
			if (!filename) {
				return blobToJson(res.data).then((res: any) => {
					return codeCheck(res, options);
				});
			}

			// 兼容IE和Edge
			if ("ActiveXObject" in window || navigator.userAgent.indexOf("Edge") > -1) {
				let nav: any = window.navigator;
				nav.msSaveBlob(res.data, decodeURIComponent(filename));
				return res;
			}

			let url = window.URL.createObjectURL(res.data);
			let a = document.createElement('a');
			a.download = decodeURIComponent(customFilename || filename);
			a.href = url;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);

			return res;
		});
}

